<template>
  <div
    class="custom-table-container"
    :class="{ 'has-many-columns': hasMoreThanTenColumns }"
  >
    <div class="table-header-actions">
      <h3 class="table-title-text">{{ tableTitle }}</h3>
      <div class="table-action-buttons" v-if="!isViewOnly">
        <b-button
          type="is-primary"
          size="is-small"
          icon-left="pencil"
          @click="editTableConfig"
        >
          Edit
        </b-button>
        <b-button
          type="is-normal"
          size="is-small"
          icon-left="content-copy"
          @click="copyTable"
        >
          Copy
        </b-button>
        <b-button
          type="is-danger"
          size="is-small"
          icon-left="delete"
          @click="deleteTable"
        >
          Delete
        </b-button>
      </div>
    </div>
    <table class="custom-table">
      <thead>
        <!-- Add sub-category header column -->
        <tr>
          <th class="category-label" :rowspan="headerRows.length">
            Category
          </th>
          <th
            v-if="hasSubCategories"
            class="sub-category-label"
            :rowspan="headerRows.length"
          >
            Sub-category
          </th>
          <th class="category-rule-label" :rowspan="headerRows.length">
            Category Rule
          </th>
          <th
            v-for="(cell, cellIdx) in headerRows[0]"
            :key="tableIndex + '-header-cell-0-' + cellIdx"
            :colspan="cell.colspan"
            :rowspan="cell.rowspan"
            class="header-cell"
          >
            {{ cell.label }}
          </th>
        </tr>
        <tr
          v-for="(row, rowIdx) in headerRows.slice(1)"
          :key="tableIndex + '-header-row-' + (rowIdx + 1)"
        >
          <th
            v-for="(cell, cellIdx) in row"
            :key="tableIndex + '-header-cell-' + (rowIdx + 1) + '-' + cellIdx"
            :colspan="cell.colspan"
            :rowspan="cell.rowspan"
            class="header-cell"
          >
            {{ cell.label }}
          </th>
        </tr>
        <!-- Row: Show assigned rule for each leaf header -->
        <tr>
          <th class="category-label"></th>
          <th v-if="hasSubCategories" class="sub-category-label"></th>
          <th class="category-rule-label"></th>
          <th
            v-for="(leaf, idx) in leafHeaders"
            :key="tableIndex + '-rule-leaf-' + idx"
            class="assigned-rule-cell"
          >
            <RuleSelector
              :rules="rules"
              v-model="leaf.headerRef.selectedRule"
              placeholder="Select rule"
              :disabled="isViewOnly"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(cat, catIdx) in localCategories">
          <tr
            v-for="(subCat, subIdx) in cat.sub_categories &&
            cat.sub_categories.length
              ? cat.sub_categories
              : [null]"
            :key="tableIndex + '-' + catIdx + '-' + subIdx"
          >
            <td
              class="category-label"
              style="text-align: left;"
              v-if="subIdx === 0"
              :rowspan="
                cat.sub_categories && cat.sub_categories.length
                  ? cat.sub_categories.length
                  : 1
              "
              :title="'ID: ' + cat.id"
            >
              {{ cat.name }}
            </td>
            <template v-if="hasSubCategories">
              <td class="sub-category-label" style="text-align: left;">
                <span v-if="subCat && subCat.name">{{ subCat.name }}</span>
                <span v-else>&nbsp;</span>
              </td>
            </template>
            <td class="category-rule-cell">
              <template v-if="cat.sub_categories && cat.sub_categories.length">
                <RuleSelector
                  v-if="subCat"
                  :rules="rules"
                  v-model="subCat.selectedRules"
                  :multiple="true"
                  :disabled="isViewOnly"
                />
                <span v-else></span>
              </template>
              <RuleSelector
                v-else
                :rules="rules"
                v-model="cat.selectedRules"
                :multiple="true"
                :disabled="isViewOnly"
              />
            </td>
            <td
              v-for="(leaf, idx) in leafHeaders"
              :key="
                tableIndex + '-' + catIdx + '-' + subIdx + '-cell-leaf-' + idx
              "
              class="cell-content"
            >
              <div class="cell-rule-links">
                <!-- Category row rules (multiple) -->
                <a
                  v-for="(catRule, crIdx) in getCategoryRules(cat, subCat)"
                  :key="'cr-' + crIdx"
                  href="#"
                  class="rule-link rule-link-row"
                  @click.prevent="emitRuleClick(catRule)"
                  :title="catRule.name"
                >
                  <b-tag type="is-warning is-light" class="rule-tag">{{
                    catRule.name
                  }}</b-tag>
                </a>
                <!-- Leaf header rule (single) -->
                <a
                  v-if="getAssignedRule(leaf)"
                  href="#"
                  class="rule-link rule-link-row"
                  @click.prevent="emitRuleClick(getAssignedRule(leaf))"
                  :title="getAssignedRule(leaf).name"
                >
                  <b-tag type="is-success is-light" class="rule-tag">{{
                    getAssignedRule(leaf).name
                  }}</b-tag>
                </a>
                <!-- Fallback: cell value when no rules assigned -->
                <span
                  v-if="
                    !getCategoryRules(cat, subCat).length &&
                      !getAssignedRule(leaf)
                  "
                  :title="`Key: ${getCellKey(cat, leaf, subCat)}`"
                >
                  {{ getCellValue(cat, leaf, subCat) }}
                </span>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import RuleSelector from "./RuleSelector.vue";
import RulesManagerService from "@/services/RulesManagerService";

export default {
  name: "CustomTable",
  components: {
    RuleSelector
  },
  props: {
    tableTitle: String,
    headers: Array, // from TableConfig.vue
    initialCategories: {
      type: Array,
      default: () => []
    },
    initialCellValues: {
      type: Object,
      default: () => ({})
    },
    configIndex: {
      type: Number,
      required: true
    },
    tableIndex: {
      type: Number,
      required: true
    },
    isViewOnly: {
      type: Boolean,
      default: false
    },
    formData: {
      type: Object,
      default: null
    },
    rulesList: {
      type: Array,
      default: () => []
    },
    categoriesList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      cellValues:
        this.initialCellValues && Object.keys(this.initialCellValues).length > 0
          ? JSON.parse(JSON.stringify(this.initialCellValues))
          : {}, // { [catId_header_subHeader]: value }
      preserveInitialCellValues: !!(
        this.initialCellValues && Object.keys(this.initialCellValues).length > 0
      ), // Flag to indicate if we should use initialCellValues
      RulesManagerService: new RulesManagerService(),
      localCategories:
        this.initialCategories && this.initialCategories.length > 0
          ? JSON.parse(JSON.stringify(this.initialCategories))
          : [],
      visibleCategoryRange: { start: 0, end: 50 }, // For virtualization
      leafRuleUnwatchFns: [],
      categoryRuleUnwatchFns: [],
      // Cache for expensive method computations
      categoryRuleCache: {}, // Store getCategoryRule results
      assignedRuleCache: {}, // Store getAssignedRule results
      cellKeyCache: {}, // Store getCellKey results
      updateCellValuesTimer: null, // Debounce timer for updates
      lastRulesHash: null // Track if rules actually changed
    };
  },
  computed: {
    rules() {
      const result = this.rulesList || [];
      return result;
    },
    headerColSpan() {
      return this.leafHeaders.length;
    },
    hasSubCategories() {
      return this.localCategories.some(
        cat => cat.sub_categories && cat.sub_categories.length > 0
      );
    },
    // Returns an array of rows, each row is an array of { label, colspan, rowspan }
    headerRows() {
      // Check if headers is defined
      if (!this.headers || !Array.isArray(this.headers)) {
        return [];
      }
      // Helper: count leaf nodes under a header
      function countLeafs(header) {
        if (!header.subHeaders || header.subHeaders.length === 0) return 1;
        return header.subHeaders.reduce((sum, sub) => sum + countLeafs(sub), 0);
      }
      // Find max depth
      function getDepth(headers) {
        if (!headers || headers.length === 0) return 0;
        return 1 + Math.max(...headers.map(h => getDepth(h.subHeaders || [])));
      }
      const depth = getDepth(this.headers);
      // Build rows
      const rows = Array.from({ length: depth }, () => []);
      function fillRows(headers, level) {
        headers.forEach(header => {
          const hasChildren = header.subHeaders && header.subHeaders.length > 0;
          const cell = {
            label: header.name,
            colspan: hasChildren ? countLeafs(header) : 1,
            rowspan: hasChildren ? 1 : depth - level
          };
          rows[level].push(cell);
          if (hasChildren) fillRows(header.subHeaders, level + 1);
        });
      }
      fillRows(this.headers, 0);
      return rows;
    },
    leafHeaders() {
      // Check if headers is defined
      if (!this.headers || !Array.isArray(this.headers)) {
        return [];
      }
      // Recursively flatten all leaf headers (no subHeaders)
      const result = [];
      const traverse = (header, labelPath = []) => {
        if (!header.subHeaders || header.subHeaders.length === 0) {
          result.push({
            headerRef: header,
            fullLabel: [...labelPath, header.name].join(" / "),
            rules: header.rules || []
          });
        } else {
          header.subHeaders.forEach(sub =>
            traverse(sub, [...labelPath, header.name])
          );
        }
      };
      this.headers.forEach(h => traverse(h));
      return result;
    },
    hasMoreThanTenColumns() {
      // Account for Category, Sub-category (if exists), and Category Rule columns
      const staticColumns = 3; // Category, Sub-category, Category Rule
      const totalColumns = staticColumns + this.leafHeaders.length;
      return totalColumns > 10;
    }
  },
  mounted() {
    // Defer heavy initialization to not block UI render
    // Use requestIdleCallback if available, otherwise setTimeout
    const deferredInit = () => {
      // Ensure each leaf headerRef has a selectedRule property for reactivity (single-select, unchanged)
      this.leafHeaders.forEach(leaf => {
        if (typeof leaf.headerRef.selectedRule === "undefined") {
          this.$set(leaf.headerRef, "selectedRule", null);
        } else if (leaf.headerRef.selectedRule !== null) {
          // If selectedRule exists, find the matching rule object from the rules list
          const existingRule = leaf.headerRef.selectedRule;
          // Try to find the rule in the rules list by name or id
          let matchedRule = null;
          if (existingRule && typeof existingRule === "object") {
            // If it's an object with a name property, search for matching rule
            if (existingRule.name) {
              matchedRule = this.rules.find(r => r.name === existingRule.name);
            } else if (existingRule.id) {
              matchedRule = this.rules.find(r => r.id === existingRule.id);
            }
          }
          // Use matched rule from rules list, or keep the existing rule
          const ruleToSet = matchedRule || existingRule;
          this.$set(leaf.headerRef, "selectedRule", ruleToSet);
        }
      });

      // Use provided categories in order of priority:
      // 1. initialCategories from table data (has selectedRule)
      // 2. Fetch from API
      if (this.initialCategories && this.initialCategories.length > 0) {
        this.localCategories = JSON.parse(
          JSON.stringify(this.initialCategories)
        );
        // Defer rule matching to not block initial render
        this.$nextTick(() => {
          // Ensure selectedRules (array) properties are reactive; migrate legacy selectedRule
          this.localCategories.forEach(cat => {
            // Migrate legacy single selectedRule → selectedRules array
            if (!Array.isArray(cat.selectedRules)) {
              const legacy = cat.selectedRule ? [cat.selectedRule] : [];
              this.$set(cat, "selectedRules", legacy);
            }
            // Re-match each rule object against the loaded rules list
            const matchedCatRules = cat.selectedRules.map(sr => {
              return (
                (sr.name && this.rules.find(r => r.name === sr.name)) ||
                (sr.id && this.rules.find(r => r.id === sr.id)) ||
                sr
              );
            });
            this.$set(cat, "selectedRules", matchedCatRules);

            if (cat.sub_categories && cat.sub_categories.length > 0) {
              cat.sub_categories.forEach(subCat => {
                if (!Array.isArray(subCat.selectedRules)) {
                  const legacySub = subCat.selectedRule
                    ? [subCat.selectedRule]
                    : [];
                  this.$set(subCat, "selectedRules", legacySub);
                }
                const matchedSubRules = subCat.selectedRules.map(sr => {
                  return (
                    (sr.name && this.rules.find(r => r.name === sr.name)) ||
                    (sr.id && this.rules.find(r => r.id === sr.id)) ||
                    sr
                  );
                });
                this.$set(subCat, "selectedRules", matchedSubRules);
              });
            }
          });
        });
      } else {
        this.fetchCategories();
      }

      // Ensure selectedRules arrays exist on freshly-fetched or newly-initialised categories
      this.$nextTick(() => {
        this.localCategories.forEach(cat => {
          if (!Array.isArray(cat.selectedRules)) {
            this.$set(
              cat,
              "selectedRules",
              cat.selectedRule ? [cat.selectedRule] : []
            );
          }
          if (cat.sub_categories) {
            cat.sub_categories.forEach(subCat => {
              if (!Array.isArray(subCat.selectedRules)) {
                this.$set(
                  subCat,
                  "selectedRules",
                  subCat.selectedRule ? [subCat.selectedRule] : []
                );
              }
            });
          }
        });
      });

      // Trigger initial cell values update
      this.$nextTick(() => {
        // Only regenerate cellValues if they weren't provided initially
        // This ensures initialCellValues are preserved
        if (!this.preserveInitialCellValues) {
          this.updateAllCellValues();
        }
        // Disable watchers for large datasets to prevent freezing
        // Large tables (500+ categories) cause excessive updates
        if (this.localCategories.length < 500) {
          this.setupLeafRuleWatchers();
        }
      });
    };

    // Use requestIdleCallback for better performance, fallback to setTimeout
    if (window.requestIdleCallback) {
      window.requestIdleCallback(deferredInit, { timeout: 100 });
    } else {
      setTimeout(deferredInit, 0);
    }
  },
  beforeDestroy() {
    // Clean up all watchers and timers on component destruction
    if (this.leafRuleUnwatchFns) {
      this.leafRuleUnwatchFns.forEach(unwatch => unwatch());
      this.leafRuleUnwatchFns = [];
    }
    if (this.updateCellValuesTimer) {
      clearTimeout(this.updateCellValuesTimer);
      this.updateCellValuesTimer = null;
    }
    // Clear caches
    this.categoryRuleCache = {};
    this.assignedRuleCache = {};
    this.cellKeyCache = {};
  },
  watch: {
    rulesList: {
      handler(newVal) {
        // When rules list is updated, re-match selectedRules with the new rules list
        if (newVal && newVal.length > 0) {
          // For large datasets, skip expensive updates to prevent freezing
          if (this.localCategories.length > 500) {
            return;
          }

          // Update leaf header rules
          this.leafHeaders.forEach(leaf => {
            if (leaf.headerRef.selectedRule) {
              let matchedRule = null;
              if (leaf.headerRef.selectedRule.name) {
                matchedRule = newVal.find(
                  r => r.name === leaf.headerRef.selectedRule.name
                );
              } else if (leaf.headerRef.selectedRule.id) {
                matchedRule = newVal.find(
                  r => r.id === leaf.headerRef.selectedRule.id
                );
              }
              if (matchedRule) {
                this.$set(leaf.headerRef, "selectedRule", matchedRule);
              }
            }
          });

          // Update category and subcategory rules (selectedRules array)
          this.localCategories.forEach(cat => {
            if (!Array.isArray(cat.selectedRules)) {
              this.$set(
                cat,
                "selectedRules",
                cat.selectedRule ? [cat.selectedRule] : []
              );
            }
            const rematchedCat = cat.selectedRules.map(
              sr =>
                (sr.name && newVal.find(r => r.name === sr.name)) ||
                (sr.id && newVal.find(r => r.id === sr.id)) ||
                sr
            );
            this.$set(cat, "selectedRules", rematchedCat);

            if (cat.sub_categories && cat.sub_categories.length > 0) {
              cat.sub_categories.forEach(subCat => {
                if (!Array.isArray(subCat.selectedRules)) {
                  this.$set(
                    subCat,
                    "selectedRules",
                    subCat.selectedRule ? [subCat.selectedRule] : []
                  );
                }
                const rematchedSub = subCat.selectedRules.map(
                  sr =>
                    (sr.name && newVal.find(r => r.name === sr.name)) ||
                    (sr.id && newVal.find(r => r.id === sr.id)) ||
                    sr
                );
                this.$set(subCat, "selectedRules", rematchedSub);
              });
            }
          });

          // Clear caches after update
          this.categoryRuleCache = {};
          this.assignedRuleCache = {};
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
      deep: false
    },

    localCategories: {
      handler() {
        // Debounce updates for large datasets
        if (this.updateCellValuesTimer) {
          clearTimeout(this.updateCellValuesTimer);
        }
        this.updateCellValuesTimer = setTimeout(() => {
          this.updateAllCellValues();
        }, 100); // Wait 100ms before updating to batch changes
      },
      deep: false
    }
  },
  methods: {
    editTableConfig() {
      this.$emit("edit-table", {
        configIndex: this.configIndex,
        tableIndex: this.tableIndex,
        tableName: this.tableTitle,
        headers: this.headers,
        categories: this.localCategories,
        cellValues: this.cellValues
      });
    },
    deleteTable() {
      this.$buefy.dialog.confirm({
        title: "Delete Table",
        message: "Are you sure you want to delete this table?",
        confirmText: "Delete",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => {
          this.$emit("delete-table", {
            configIndex: this.configIndex,
            tableIndex: this.tableIndex
          });
        }
      });
    },
    copyTable() {
      // Emit event to parent to open config modal with current table data
      this.$emit("copy-table", {
        configIndex: this.configIndex,
        tableIndex: this.tableIndex,
        tableName: this.tableTitle,
        headers: JSON.parse(JSON.stringify(this.headers)),
        categories: JSON.parse(JSON.stringify(this.localCategories)),
        cellValues: JSON.parse(JSON.stringify(this.cellValues))
      });
    },
    setupLeafRuleWatchers() {
      // Remove old watchers if any
      if (this.leafRuleUnwatchFns) {
        this.leafRuleUnwatchFns.forEach(unwatch => unwatch());
      }
      this.leafRuleUnwatchFns = [];
      this.leafHeaders.forEach(leaf => {
        const unwatch = this.$watch(
          () => leaf.headerRef.selectedRule,
          () => {
            this.updateAllCellValues();
          }
        );
        this.leafRuleUnwatchFns.push(unwatch);
      });
    },
    fetchCategories() {
      // Fetch categories from API if formData is available
      if (
        this.formData &&
        this.formData.country &&
        this.formData.stream &&
        this.formData.scheme
      ) {
        this.RulesManagerService.getReportCategories(this.formData).subscribe(
          response => {
            if (response && response.categories_fees) {
              this.localCategories = response.categories_fees.map(cat => ({
                ...cat,
                rules: [],
                selectedRules: Array.isArray(cat.selectedRules)
                  ? cat.selectedRules
                  : cat.selectedRule
                  ? [cat.selectedRule]
                  : []
              }));
              this.$nextTick(() => {
                this.updateAllCellValues();
              });
            }
          },
          error => {
            console.error("Error fetching categories:", error);
            this.$buefy.toast.open({
              message: "Failed to load categories",
              type: "is-danger"
            });
          }
        );
      } else {
        // No formData available, just update with empty categories
        this.$nextTick(() => {
          this.updateAllCellValues();
        });
      }
    },
    getAvailableRules() {
      // Return all rules for dropdown - no longer filtering by header/sub
      return this.rules || [];
    },
    getCellKey(cat, leaf, subCat) {
      // Generate a unique key: categoryId_categoryName_fullHeaderPath_subCategoryName
      // Using fullLabel from leaf to ensure child of child headers are uniquely identified
      const catId = cat.id || cat.name || "cat";
      const catName = cat.name || "";
      const headerFullPath =
        leaf.fullLabel || (leaf.headerRef && leaf.headerRef.name) || "";
      const subCatName = subCat && subCat.name ? subCat.name : "no-subcat";
      // Replace both spaces and slashes with underscores for a clean key
      return `${catId}_${catName}_${headerFullPath}_${subCatName}`
        .replace(/\s+/g, "_")
        .replace(/\//g, "_");
    },
    getCellValue(cat, leaf, subCat) {
      const key = this.getCellKey(cat, leaf, subCat);
      const value = this.cellValues[key];
      return value;
    },
    getResolvedRuleName(cat, subCat, leaf) {
      // Merge: all category row rules + the leaf header rule
      const categoryRules = this.getCategoryRules(cat, subCat);
      const categoryPart = categoryRules
        .map(r => r.name)
        .filter(Boolean)
        .join(" / ");

      let assignedRule = "";
      if (
        leaf &&
        leaf.headerRef &&
        leaf.headerRef.selectedRule &&
        leaf.headerRef.selectedRule.name
      ) {
        assignedRule = leaf.headerRef.selectedRule.name;
      }

      const result =
        categoryPart && assignedRule
          ? categoryPart + " / " + assignedRule
          : categoryPart || assignedRule || "";

      return result;
    },

    getCategoryRules(cat, subCat) {
      // Returns an array of rule objects assigned to this category/sub-category row.
      // NOTE: no caching here — selectedRules is reactive and must always reflect current state.
      if (!cat) return [];

      if (subCat && Array.isArray(subCat.selectedRules)) {
        return subCat.selectedRules;
      } else if (subCat && subCat.selectedRule) {
        return [subCat.selectedRule]; // legacy fallback
      } else if (Array.isArray(cat.selectedRules)) {
        return cat.selectedRules;
      } else if (cat.selectedRule) {
        return [cat.selectedRule]; // legacy fallback
      }
      return [];
    },
    // Keep old name as alias so any external consumers still work
    getCategoryRule(cat, subCat) {
      const rules = this.getCategoryRules(cat, subCat);
      return rules.length ? rules[0] : null;
    },

    getAssignedRule(leaf) {
      // Use cache to avoid recomputation
      if (!leaf) return null;
      // Use fullLabel (full path) so leaves with the same name at different
      // paths (e.g. "Primary / RED" vs "Shipment / RED") get distinct keys.
      const cacheKey = leaf.fullLabel || `${leaf.headerRef?.name || "unknown"}`;
      if (this.assignedRuleCache[cacheKey] !== undefined) {
        return this.assignedRuleCache[cacheKey];
      }

      let rule = null;
      if (leaf && leaf.headerRef && leaf.headerRef.selectedRule) {
        rule = leaf.headerRef.selectedRule;
      }

      this.assignedRuleCache[cacheKey] = rule;
      return rule;
    },

    emitRuleClick(rule) {
      if (!rule) return;
      this.$emit("view-rule", rule);
    },

    updateAllCellValues() {
      // Clear caches when updating cell values to prevent stale data
      this.categoryRuleCache = {};
      this.assignedRuleCache = {};
      this.cellKeyCache = {};

      // For each category, sub-category, and leaf, update cellValues with merged rule name
      this.localCategories.forEach(cat => {
        const subCats =
          cat.sub_categories && cat.sub_categories.length
            ? cat.sub_categories
            : [null];
        subCats.forEach(subCat => {
          this.leafHeaders.forEach(leaf => {
            const key = this.getCellKey(cat, leaf, subCat);
            const value = this.getResolvedRuleName(cat, subCat, leaf);
            this.$set(this.cellValues, key, value);
          });
        });
      });
    }
  }
};
</script>

<style scoped>
.custom-table-container {
  margin-bottom: 32px;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}
.button.is-normal {
  font-size: 0.75rem !important;
}
.table-header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-bottom: none;
}
.table-title-text {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #363636;
  padding: 10px 0;
}
.table-action-buttons {
  display: flex;
  gap: 8px;
}
.category-rule-label {
  font-weight: bold;
  text-align: center;
  font-size: 0.9rem;
}
.category-rule-cell {
  background: #fff;
  padding: 4px 6px;
  max-width: 180px;
}
.assigned-rule-cell {
  max-width: 180px;
  font-size: 0.85rem;
}
.custom-table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e0e0e0;
  border-top: none;
}
.custom-table-container.has-many-columns .custom-table {
  display: block;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  table-layout: auto;
  width: auto;
}
.custom-table th,
.custom-table td {
  border: 1px solid #e0e0e0;
  padding: 6px 8px;
  text-align: center;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.2;
  vertical-align: middle;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  min-width: 140px;
  max-width: 200px;
}
.custom-table .cell-content {
  min-width: 140px;
  max-width: 150px;
  overflow: hidden;
}
.table-title {
  font-weight: bold;
  font-size: 1.2em;
}
.header-cell,
.sub-header-cell {
  font-weight: bold;
}
.category-label {
  font-weight: bold;
  text-align: left;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
}
.sub-category-label {
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
  text-align: left;
}
.cell-rule-links {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 0.85rem;
  line-height: 1.3;
}
.rule-link-row {
  display: inline-flex;
  text-decoration: none;
  max-width: 100%;
}
.rule-link-row:hover .rule-tag,
.rule-link-row:focus .rule-tag {
  filter: brightness(0.93);
}
.rule-tag {
  display: inline-flex !important;
  width: auto !important;
  max-width: 100%;
  white-space: normal !important;
  word-break: break-word;
  height: auto !important;
  line-height: 1.3 !important;
  padding: 2px 6px !important;
  font-size: 0.78rem !important;
  cursor: pointer;
}
/* Performance Optimizations - Reduce repaints/reflows */
.custom-table {
  will-change: scroll-position;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
}
.custom-table tbody tr {
  contain: layout style paint;
}
.cell-content {
  contain: layout style paint;
}
</style>
